import React, { useState } from "react";
import { Grid, Paper, Typography, Button } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CcsStatusUpdateSection from "../../prescription_lifecycle/components/prescription/CcsStatusUpdateSection";
import RphDecisionApprovalSection from "../../pacman/components/rph_decision/RphDecisionApprovalSection";
import { DataAccordion, PrescriptionLookupStatus, StyledActionButton } from "../components";
import { COLORS, BUTTON_STYLES } from "../styles/theme";
import { INewRxStepHandoff } from "../types/PrescriptionIngestionTypes";
import { usePrescriptionLookup } from "../hooks/usePrescriptionLookup";

interface IProps {
  // Context carried from the "Ingest NewRx to UPMS" step via Continue. Absent when
  // the user lands here directly, in which case the embedded sections' own
  // Person Id / Profile Id fields are used to enter the values manually.
  handoff?: INewRxStepHandoff;
  // Advances to the Create Order step, carrying the personId forward.
  onContinue?: (handoff: INewRxStepHandoff) => void;
}

const nextStepButtonStyle = BUTTON_STYLES.nextStep;

/**
 * Step 3 of the NewRx Operations flow: approve the prescription (CCS Status Update)
 * or approve an Rx-less OTC (RPH Decision). Reuses the existing CCS / RPH sections,
 * prefilled from the handoff when reached via Continue. Only one approval is shown
 * at a time; on success it collapses into a summary accordion and reveals a
 * "Continue to Create Order" button.
 */
const ApprovePrescriptionStepSection: React.FC<IProps> = ({
  handoff,
  onContinue,
}) => {
  const [showCcs, setShowCcs] = useState(false);
  const [showRph, setShowRph] = useState(false);
  const [ccsResponse, setCcsResponse] = useState<any>(null);
  const [rphResponse, setRphResponse] = useState<any>(null);
  const lookup = usePrescriptionLookup(handoff);

  const personId = handoff?.personId;
  const profileId = handoff?.profileId;
  const prescriptionId = lookup.prescriptionId;

  const stepComplete = !!ccsResponse || !!rphResponse;

  // Once an approval succeeds, render the result as plain summary accordion(s)
  // matching steps 1-2 (no orange action panel), plus the Continue prompt.
  if (stepComplete) {
    return (
      <Grid container direction="column" spacing={3} style={{ marginTop: 8 }}>
        {ccsResponse && (
          <DataAccordion
            title="3. Approve Prescription"
            data={ccsResponse}
            defaultExpanded
          />
        )}
        {rphResponse && (
          <DataAccordion
            title="3. Approve Rxless OTC"
            data={rphResponse}
            defaultExpanded
          />
        )}

        {onContinue && (
          <Grid item xs={12} style={{ width: "100%", maxWidth: 1200 }}>
            <Alert severity="info" style={{ marginBottom: 16 }}>
              <Typography variant="body2" style={{ fontWeight: "bold" }}>
                Next Step: Create Order
              </Typography>
              <Typography variant="body2">
                Continue to create an order for this customer.
              </Typography>
            </Alert>
            <StyledActionButton
              label="Continue to Create Order"
              onClick={() =>
                onContinue({ ...handoff, personId, prescriptionId })
              }
              color={COLORS.orange}
              startIcon={<ArrowForwardIcon />}
            />
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <Grid container direction="column" spacing={3} style={{ marginTop: 8 }}>
      <Grid item xs={12} style={{ width: "100%", maxWidth: 1200 }}>
        <Paper style={{ padding: 24, backgroundColor: "#fff3e0" }}>
          <Typography variant="h6" gutterBottom>
            3. Approve Prescription / OTC
          </Typography>

          <div style={{ marginBottom: 16 }}>
            <Alert severity="warning">
              Please continue with this step after 2 mins once step 2 above is
              done (the prescription needs time to process in UPMS).
            </Alert>
          </div>

          {!handoff && (
            <div style={{ marginBottom: 16 }}>
              <Alert severity="info">
                Enter the Person Id (for Approve Prescription) or Profile Id (for
                Approve Rxless OTC) below. These come from the customer created
                during the Upload / Ingest steps.
              </Alert>
            </div>
          )}

          <PrescriptionLookupStatus lookup={lookup} drugDescription={handoff?.drugDescription} />

          {!lookup.isLookingUp && (
            <>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {!showRph && (
                  <Button
                    variant="outlined"
                    aria-expanded={showCcs}
                    onClick={() => setShowCcs((show) => !show)}
                    style={nextStepButtonStyle}
                  >
                    {showCcs ? "Hide" : "Approve Prescription"}
                  </Button>
                )}
                {!showCcs && (
                  <Button
                    variant="outlined"
                    aria-expanded={showRph}
                    onClick={() => setShowRph((show) => !show)}
                    style={nextStepButtonStyle}
                  >
                    {showRph ? "Hide" : "Approve Rxless OTC"}
                  </Button>
                )}
              </div>

              {showCcs && (
                <div style={{ marginTop: 24 }}>
                  <CcsStatusUpdateSection
                    initialPersonId={personId}
                    initialPrescriptionId={prescriptionId}
                    submitColor={COLORS.orange}
                    onSuccess={(response, submittedPrescriptionId) => {
                      setCcsResponse(response);
                      // Carry the actually-submitted prescriptionId forward (covers
                      // the case where the lookup found no match and the user typed
                      // one in).
                      if (submittedPrescriptionId) {
                        lookup.setPrescriptionId(submittedPrescriptionId);
                      }
                      setShowCcs(false);
                    }}
                  />
                </div>
              )}

              {showRph && (
                <div style={{ marginTop: 24 }}>
                  <RphDecisionApprovalSection
                    initialProfileId={profileId}
                    submitColor={COLORS.orange}
                    onSuccess={(response) => {
                      setRphResponse(response);
                      setShowRph(false);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ApprovePrescriptionStepSection;
